import { FenceGroup, ItemType, iFenceDetails, iFullStoreState, iList, iTag } from '../../../../../shared/interfaces';
import React, { useEffect, useState } from 'react';
import { Set, is } from 'immutable';
import { equals, values } from 'ramda';
import { getAllDevicesTag, isMobile } from '../../../../../shared/helpers';
import { Actions as FenceActions } from '../../../../../stores/reducers/fences-reducers';

import FenceAccordion from './FenceAccordion';
import { IButtonsProps } from './ShowVisTagsItems.interfaces';
import { keyGen } from '../../../../../shared/firebase';
import { selectDevices } from '../../../../../stores/reducers/devicesData/selector';
import styles from './ShowVisTagsItems.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useVisRegions } from '../../../../../stores/reducers/map-region-reducers';
import { useFenceManagement } from '../../../../../hooks/useFenceManagement';
import FenceSearchBar from '../FenceSearchBar';
import FencePagination from '../FencePagination';
import FenceButton from '../FenceButton';

const ShowVisTagsItems = ({ itemId }: IButtonsProps): JSX.Element => {
    const [regions, { add, remove }] = useVisRegions();

    const devices = useSelector(selectDevices);
    const dispatch = useDispatch();

    const isGlobalPage = itemId === null;

    enum ActionSelectAllType {
        _,
        SELECT,
        UNSELECT,
    }

    const [selectAllButtonType, setSelectAllButtonType] = useState<ActionSelectAllType>(
        ActionSelectAllType.SELECT
    );

    const tags = useSelector<iFullStoreState, iList<iTag>>(
        (s) => s.general.tags,
        equals
    );

    const newTags = { ...tags, ...getAllDevicesTag(devices) };

    const showedTagsIds = useSelector<iFullStoreState, Set<string>>(
        (state) => state.tagsDevicesMap.showedTagsIds,
        is
    );

    const tagFences = useSelector<iFullStoreState, iList<iFenceDetails>>(
        (state) => state.general.fences?.[ItemType.tag] ?? {},
        equals
    );

    // Used for specific page
    const showedTagsDetails = itemId && newTags[itemId] ? [newTags[itemId].details] : [];
    const fencesToShowOnMap: iFenceDetails[] = values(tagFences[itemId] || {});

    // Select All / Clear All
    const allToggle = () => {
        const select = selectAllButtonType === ActionSelectAllType.SELECT;

        fencesToShowOnMap.forEach(fence => {
            select ? add(fence) : remove(fence.id);
        });
    };

    // Check if all fences are selected
    const changeSelectAllButtonType = () => {
        const isAllSelected =
            Object.keys(regions).length === fencesToShowOnMap.length &&
            fencesToShowOnMap.length > 0;

        setSelectAllButtonType(
            isAllSelected ? ActionSelectAllType.UNSELECT : ActionSelectAllType.SELECT
        );
    };

    useEffect(changeSelectAllButtonType, [regions, fencesToShowOnMap]);

    // Tags list for global page
    const allTagDetails = Object.values(newTags).map(tag => tag.details);

    // Used for global page
    const {
        currentPage,
        filter,
        filteredItems: filteredTags,
        paginatedItems: paginatedTags,
        totalPages,
        handleSearchChange,
        handlePageChange,
    } = useFenceManagement(allTagDetails);

    return (
        <div className={`TaggedItems ${isMobile ? styles.taggedItemsMobile : ''}`}>
            {/* Global Search */}
            {isGlobalPage && (
                <FenceSearchBar
                    filter={filter}
                    onFilterChange={handleSearchChange}
                    resultsCount={filteredTags.length}
                    placeholder="Search tags..."
                    wantedItems="tag"
                />
            )}

            {/* Tags list */}
            <div className={styles.tagsList}>
                {(isGlobalPage ? paginatedTags : showedTagsDetails).map(tagDetails => (
                    <FenceAccordion
                        key={tagDetails.id}
                        tagDetails={tagDetails}
                        isGlobalPage={isGlobalPage}
                    />
                ))}
            </div>

            {/* Pagination */}
            {isGlobalPage && totalPages > 1 && (
                <FencePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    totalItems={filteredTags.length}
                />
            )}

            {/* Buttons */}
            {!isGlobalPage && (
                <div className={styles.buttonsBlock}>
                    {/* Select All */}
                    <FenceButton
                        type="selectAll"
                        onClick={allToggle}
                        isSelected={selectAllButtonType === ActionSelectAllType.UNSELECT}
                    />

                    {/* Add Fence */}
                    <FenceButton
                        type="addFence"
                        linkTo={`/${ItemType.tag}/${itemId}/${FenceGroup.FENCE}/${keyGen()}/edit`}
                        onClick={() => dispatch(FenceActions.SET_BACKUP_STATUS(true))}
                    />
                </div>
            )}
        </div>
    );
};

export default ShowVisTagsItems;
